/* eslint-disable no-unused-vars */
import React, { useMemo, useRef, useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
// (3D temporarily disabled for React 19 compatibility)
// import { Canvas, useFrame } from "@react-three/fiber";
// import { Float, Environment, Html } from "@react-three/drei";
import { AuthContext } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import axios from "../api/axiosClient";
import PublicNavbar from "../components/public/PublicNavbar";
import PublicFooter from "../components/public/PublicFooter";
import { useTranslation } from "react-i18next";

/**
 * Hairchitech – Fancy Pricing Page
 * --------------------------------------------------------------
 * Highlights
 * - Luxury hero with real 3D gold animation (react-three-fiber)
 * - Glass cards, gradient accents, smooth micro‑interactions
 * - Plan switcher (Monthly/Annual), feature comparison, sticky CTA
 * - Works with existing `handleSubscribe` (Stripe Checkout routing)
 * - Mobile‑first, a11y‑friendly, reduced‑motion support
 *
 * NPM (run once):
 *   npm i @react-three/fiber @react-three/drei framer-motion
 *
 * Tailwind: uses your existing setup.
 * Logo: public/images/logos/Hairchitech_Logo1.png
 */

const LOGO = "/images/logos/Hairchitech_Logo1.png";
const GOLD = "#7d6633"; // brand gold
const GOLD_DARK = "#7d6633";

// ----- 3D: simple elegant gold knot that floats & glows ----- //
function GoldKnot() { return null; }

// plan data (kept from your current logic, expanded for UI labels)
const PLANS = {
  barbershop: {
    monthly: 39,
    annual: 450,
    name: "Barbershop",
    tag: "For barbers & studios",
    description: "Complete barbershop management",
    features: [
      "500 AI hairstyling images",
      "Full booking system",
      "QR code generator",
      "Inventory management (Basic)",
      "Client management",
      "Basic analytics",
    ],
    highlight: "Best for solo shops",
  },
  salon: {
    monthly: 39,
    annual: 450,
    name: "Salon Base",
    tag: "For growing salons",
    description: "Essential salon management tools",
    features: [
      "500 AI hairstyling images",
      "Full booking system",
      "QR code generator",
      "Inventory management (Advanced)",
      "Client management",
      "Basic analytics",
    ],
    highlight: "Most popular",
  },
  salonPremium: {
    monthly: 51,
    annual: 600,
    name: "Salon + Add‑ons",
    tag: "Makeup & Nails included",
    description: "Salon with makeup & nails AI",
    features: [
      "600 AI hairstyling images",
      "Full booking system",
      "QR code generator",
      "Inventory management (Advanced)",
      "Client management",
      "Makeup & Nails AI",
      "Enhanced salon profile",
      "Priority support",
    ],
    highlight: "All‑in bundle",
  },
};

const FAQ = [
  {
    q: "When does my quota reset?",
    a: "Quotas reset monthly on the anniversary of your subscription start date. Annual plans keep monthly quota cadence.",
  },
  { q: "Can I switch plans later?", a: "Yes. Upgrades/downgrades take effect immediately with prorated billing." },
  { q: "What if I exceed my quota?", a: "Purchase add‑on credits at $0.10 per AI transformation, or upgrade your plan." },
  { q: "What's your refund policy?", a: "14‑day money‑back guarantee for new subscriptions. No questions asked." },
  { q: "Is the Makeup & Nails add‑on optional?", a: "Yes. It's included in Salon + Add‑ons, optional for Salon Base via upgrade." },
  { q: "Do you offer custom integrations?", a: "Yes—API & custom integrations for enterprise. Contact sales." },
  { q: "What payment methods do you accept?", a: "All major cards. Annual invoices available for businesses." },
];

export default function PricingPageFancy() {
  const { t } = useTranslation('marketing');
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { login, user } = useContext(AuthContext);

  const [annual, setAnnual] = useState(false);
  const [faqOpen, setFaqOpen] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [showSignInPanel, setShowSignInPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');

  const savePct = (p) => Math.round(((p.monthly * 12 - p.annual) / (p.monthly * 12)) * 100);

  const handleSubscribe = (planKey) => {
    const params = new URLSearchParams();
    params.set("plan", planKey);
    params.set("annual", annual ? "1" : "0");
    if (planKey === "salonPremium") params.set("addon", "1");
    navigate(`/checkout?${params.toString()}`);
  };

  const openSignInPanel = () => {
    setShowSignInPanel(true);
    document.body.style.overflow = 'hidden';
  };

  const closeSignInPanel = () => {
    setShowSignInPanel(false);
    document.body.style.overflow = 'auto';
  };

  const handleSignInClick = (e) => {
    e.preventDefault();
    openSignInPanel();
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const res = await axios.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });
      
      if (res.data?.ok === false || !res.data?.token) {
        if (res.data?.must_set_password) {
          setError('Please set your password using the email we sent you.');
        } else {
          setError('Email or password is incorrect. Please try again.');
        }
        setLoading(false);
        return;
      }

      const { token, user, redirect } = res.data;
      login(user, token);
      
      closeSignInPanel();
      setFormData({ name: '', email: '', password: '' });
      
      if (redirect) {
        navigate(redirect);
      } else {
        const role = user.role?.toLowerCase();
        if (role === 'superadmin') {
          navigate('/superadmin');
        } else if (role === 'shop_admin' || role === 'barber') {
          navigate('/admin');
        } else if (role === 'client') {
          navigate('/app/book');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      if (err?.response?.data?.must_set_password) {
        setError('Please set your password using the email we sent you.');
      } else {
        setError('Email or password is incorrect. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const res = await axios.post('/auth/register', formData);
      setActiveTab('login');
      setFormData({ name: '', email: '', password: '' });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  // Close panel with Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSignInPanel) {
        closeSignInPanel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showSignInPanel]);

  // Cleanup: ensure body overflow is reset when component unmounts or panel closes
  useEffect(() => {
    if (!showSignInPanel) {
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showSignInPanel]);

  return (
    <div className="min-h-screen w-full bg-zinc-950 text-white">
      {/* Sign-in Panel */}
      <div className={`fixed top-0 right-0 h-full w-full md:w-1/2 lg:w-1/2 z-[9999] transform transition-transform duration-500 ease-out ${
        showSignInPanel ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="h-full relative overflow-hidden">
          {/* Background with gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}></div>
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-white transform skew-y-6 origin-bottom-left"></div>
          
          {/* Content Container */}
          <div className="relative h-full flex items-center justify-center p-4 sm:p-6 md:p-8">
            {/* White Login Card */}
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
              {/* Header */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="flex items-center justify-center mb-3 sm:mb-4">
                  <Link to="/">
                    <img src={LOGO} alt="Hairchitech" className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 cursor-pointer" />
                  </Link>
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  {t('marketing.sign_in_title')}
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm">
                  {t('marketing.sign_in_subtitle')}
                </p>
              </div>

              {/* Sign In Header - No tabs, just sign in */}
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg font-semibold text-gray-900 text-center">{t('marketing.sign_in')}</h2>
              </div>

              {/* Login Form Only */}
              <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
                {error && <p className="text-red-500 text-xs sm:text-sm text-center">{error}</p>}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    {t('marketing.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7d6633] focus:border-[#7d6633] transition-all duration-200 text-sm sm:text-base"
                    placeholder={t('marketing.email_placeholder')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    {t('marketing.password')}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7d6633] focus:border-[#7d6633] transition-all duration-200 text-sm sm:text-base"
                    placeholder={t('marketing.password_placeholder')}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 sm:py-3 px-4 rounded-lg font-medium text-white transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  style={{ background: '#7d6633' }}
                >
                  {loading ? t('marketing.signing_in') : t('marketing.sign_in_btn')}
                </button>

                <div className="text-center pt-3 sm:pt-4">
                  <p className="text-xs sm:text-sm text-gray-600">
                    <a href="/forgot-password" style={{ color: '#7d6633' }} className="hover:opacity-80 font-medium transition-colors">
                      {t('marketing.forgot_password')}
                    </a>
                  </p>
                </div>

                {/* New User CTA */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs sm:text-sm text-gray-600 text-center mb-3">
                    <strong>{t('marketing.new_to_hairchitech')}</strong>
                  </p>
                  <p className="text-xs text-gray-500 text-center mb-3">
                    {t('marketing.choose_plan')}
                  </p>
                  <button
                    type="button"
                    onClick={closeSignInPanel}
                    className="w-full py-2 px-4 rounded-lg font-medium border-2 transition-all duration-200"
                    style={{ borderColor: '#7d6633', color: '#7d6633' }}
                  >
                    {t('marketing.view_pricing_plans')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={closeSignInPanel}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white hover:text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors z-10"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <PublicNavbar onSignInClick={handleSignInClick} />

      {/* HERO + PLANS COMBINED */}
      <section className="relative overflow-hidden pt-20 pb-12">
        {/* Luxury Gradient Background - Same as HomePage */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-yellow-900/10 to-transparent" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.1) 0%, rgba(125, 102, 51, 0.05) 50%, transparent 100%)' }}></div>

        {/* Diagonal stripe background */}
        <div
          className="absolute top-0 right-0 h-[150%] w-1 z-0 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom left, rgba(201,160,56,0.3) 0%, transparent 80%)',
            transform: 'translate(50%, -50%) rotate(45deg)',
            transformOrigin: 'top right'
          }}
        ></div>

        {/* Abstract Shapes */}
        <div className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.1) 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201, 160, 56, 0.15) 0%, transparent 70%)' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl"></div>

        {/* Floating Abstract Lines */}
        <div className="absolute top-40 left-20 w-64 h-1 rounded-full transform rotate-45" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.3) 0%, transparent 100%)' }}></div>
        <div className="absolute bottom-40 right-20 w-48 h-1 rounded-full transform -rotate-12" style={{ background: 'linear-gradient(90deg, rgba(201, 160, 56, 0.2) 0%, transparent 100%)' }}></div>
        
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-16 z-10">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold leading-tight"
            >
              {t('marketing.pricing_hero_title')}
            </motion.h1>
            

            {/* Billing Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 backdrop-blur-md px-2 py-2"
            >
              <button
                onClick={() => setAnnual(false)}
                className={`px-4 py-2 rounded-full text-sm transition ${!annual ? "bg-white text-zinc-900" : "text-white/80 hover:text-white"}`}
              >
                {t('marketing.monthly')}
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-4 py-2 rounded-full text-sm transition ${annual ? "text-zinc-900" : "text-white/80 hover:text-white"}`}
                style={annual ? { backgroundColor: '#7d6633' } : {}}
                aria-label="Toggle annual billing"
              >
                {t('marketing.annual')} <span className="ml-1 hidden sm:inline">{t('marketing.save_up_to')}</span>
              </button>
            </motion.div>
          </div>
        </div>

      {/* PLANS */}
        <div className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {Object.entries(PLANS).map(([key, p], idx) => {
            const price = annual ? p.annual : p.monthly;
            const isPopular = key === "salon";
            const saved = savePct(p);
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className={`relative rounded-3xl p-6 border bg-white/5 backdrop-blur-xl ${
                  isPopular ? "" : "border-white/10"
                }`}
                style={isPopular ? { borderColor: '#7d6633' } : {}}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold text-zinc-900 shadow" style={{ backgroundColor: '#7d6633' }}>
                      {t('marketing.most_popular')}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold">{t(`marketing.plan_${key}_name`)}</h3>
                    <p className="text-white/60 text-sm mt-1">{t(`marketing.plan_${key}_tag`)}</p>
                  </div>
                  <span className="text-xs font-medium" style={{ color: '#7d6633' }}>{t(`marketing.plan_${key}_highlight`)}</span>
                </div>

                <div className="mt-6">
                  <div className="text-4xl font-extrabold tracking-tight">
                    ${price}
                    <span className="text-base text-white/60 font-medium">{annual ? t('marketing.per_year') : t('marketing.per_month')}</span>
                  </div>
                  {annual && (
                    <div className="mt-1 text-xs" style={{ color: '#7d6633' }}>{t('marketing.save_pct_vs_monthly', { pct: saved })}</div>
                  )}
                </div>

                <ul className="mt-6 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-5 h-5 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: '#7d6633' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-white/85">{t(`marketing.plan_${key}_feature_${i}`)}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(key)}
                  disabled={loading}
                  className={`mt-8 w-full px-5 py-3 rounded-xl font-semibold transition ${
                    isPopular
                      ? "text-zinc-900 hover:-translate-y-0.5 shadow"
                      : "bg-white/10 border border-white/10 hover:bg-white/15"
                  }`}
                  style={isPopular ? { backgroundColor: '#7d6633', boxShadow: '0 10px 15px -3px rgba(125, 102, 51, 0.3)' } : {}}
                >
                  {loading ? t('marketing.processing') : t('marketing.select_plan')}
                </button>

                <p className="mt-3 text-xs text-white/50">{t('marketing.money_back')}</p>
              </motion.div>
            );
          })}
        </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-2xl font-bold">{t('marketing.compare_plans')}</h3>
              <p className="text-white/60">{t('marketing.compare_subtitle')}</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/80">
                    <th className="p-4 min-w-[220px]">{t('marketing.feature_col')}</th>
                    <th className="p-4">{t('marketing.plan_barbershop_name')}</th>
                    <th className="p-4">{t('marketing.plan_salon_name')}</th>
                    <th className="p-4">{t('marketing.plan_salonPremium_name')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    { nameKey: "marketing.compare_feature_ai_images", b: "500", s: "500", sp: "600" },
                    { nameKey: "marketing.compare_feature_booking", b: "✓", s: "✓", sp: "✓" },
                    { nameKey: "marketing.compare_feature_qr", b: "✓", s: "✓", sp: "✓" },
                    { nameKey: "marketing.compare_feature_inventory", b: "Basic", s: "Advanced", sp: "Advanced" },
                    { nameKey: "marketing.compare_feature_makeup_nails", b: "—", s: "Add‑on", sp: "✓" },
                    { nameKey: "marketing.compare_feature_support", b: "—", s: "—", sp: "✓" },
                  ].map((r, i) => (
                    <tr key={i} className="hover:bg-white/5">
                      <td className="p-4 font-medium">{t(r.nameKey)}</td>
                      {[r.b, r.s, r.sp].map((val, j) => (
                        <td key={j} className="p-4">
                          {val === "✓" ? (
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">✓</span>
                          ) : val === "—" ? (
                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-400/15 text-red-300">×</span>
                          ) : (
                            <span className="text-white/80">{val}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 bg-white/5 border-t border-white/10 text-center">
              <p className="text-white/70 mb-3">{t('marketing.custom_plan_question')}</p>
              <a href="mailto:sales@hairchitech.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-zinc-900 font-semibold shadow hover:-translate-y-0.5 transition" style={{ backgroundColor: '#7d6633' }}>
                {t('marketing.contact_sales')}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold">{t('marketing.trust_title')}</h3>
          <p className="text-white/60 mt-1">{t('marketing.trust_subtitle')}</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {[
              t('marketing.trust_label_barbershops'),
              t('marketing.trust_label_salons'),
              t('marketing.trust_label_stylists'),
              t('marketing.trust_label_nail_artists'),
              t('marketing.trust_label_makeup_artists'),
              t('marketing.trust_label_beauty_pros')
            ].map((label, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm text-white/80">{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-6 justify-center text-white/70 text-sm">
            <div className="flex items-center gap-2">{t('marketing.uptime')}</div>
            <div className="flex items-center gap-2">{t('marketing.soc2')}</div>
            <div className="flex items-center gap-2">{t('marketing.support_247')}</div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="text-3xl font-bold text-center">{t('marketing.faq_title')}</h3>
          <div className="mt-8 space-y-3">
            {FAQ.map((item, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                  className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-white/5"
                >
                  <span className="font-medium">{t(`marketing.faq_q_${i}`)}</span>
                  <motion.span animate={{ rotate: faqOpen === i ? 180 : 0 }} className="text-white/60">⌄</motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {faqOpen === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-5 pb-4 text-white/75">
                      {t(`marketing.faq_a_${i}`)}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="text-4xl font-bold">{t('marketing.cta_shop_title')}</h3>
          <p className="mt-2 text-white/70">{t('marketing.cta_shop_body')}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => handleSubscribe("salon")} className="px-8 py-4 rounded-2xl text-zinc-900 font-semibold shadow hover:-translate-y-0.5 transition" style={{ backgroundColor: '#7d6633' }}>{t('marketing.start_free_trial')}</button>
            <a href="mailto:sales@hairchitech.com" className="px-8 py-4 rounded-2xl border border-white/15 hover:bg-white/5 transition">{t('marketing.talk_to_sales')}</a>
          </div>
          <p className="mt-3 text-xs text-white/50">{t('marketing.fair_use')}</p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
